import React, { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client/react';
import cn from 'clsx';
import {
    addColumnToTab,
    AddEditCustomReportErrors,
    AddEditCustomReportState,
    copyReportDefinition,
    copyTabFromTabArray,
    copyTabs,
    CustomReportPage,
    getTab,
    removeColumnFromTab,
    removeMatchingColumns,
    updateTabByTabType,
} from '../../excel-listings/custom-report-helpers';
import CustomReportStep1Name from './step-1';
import CustomReportStep2Data from './step-2';
import CustomReportStep3Columns from './step-3';
import CreatedUpdatedInfo from '@components-clean/core/created-updated-info';
import DraggableDialog from '@components-clean/core/draggable-dialog';
import ErrorMessage from '@components-clean/core/error';
import Loading from '@components-clean/core/loading/loading';
import { useUnsavedChangesContext } from '@contexts-clean/unsaved-changes-context';
import styles from '@pages-clean/risk-assessment/risk-log/dialogs/style.module.scss';
import {
    ReportDefinitionColumnInput,
    ReportDefinitionFieldTypes,
    ReportDefinitionInput,
    ReportDefinitionTabInput,
    ReportDefinitionTabs,
} from 'types/graphql-global-types';
import { useBoolean, useTranslation } from '@hooks/index';
import Constants from '@utils/constants';
import { stringDateWithoutTimeFromUtcNonNullable } from '@utils/convertor';
import { ShowError, ShowSuccess } from '@utils/notification';
import objectEquals from '@utils/object-equals';
import { checkIfNameExist } from '@utils/validation';
import {
    ReportDefinitionAuditFields_ReportDefinition_tabs_ReportDefinitionTab as ReportDefinitionFields_tabs,
    ReportDefinitionAuditFields_ReportDefinition_tabs_ReportDefinitionTab_columns_ReportDefinitionColumn as ReportDefinitionFields_tabs_columns,
    ReportDefinitionFields,
} from '@queries/generated/fragments.generated';
import {
    reportDefinitionOptions_OpraQueries,
    upsertReportDefinition,
    upsertReportDefinitionVariables,
} from '@queries/generated/report-definition.generated';
import {
    GET_REPORT_DEFINITION_OPTIONS,
    GET_REPORT_DEFINITIONS,
    UPSERT_REPORT_DEFINITION,
} from '@queries/report-definition';
import './styles.scss';

interface ExcelListingsConfigProps {
    addExcelListingsConfigAction: boolean;
    reportDefinition?: ReportDefinitionFields;
    reportDefinitions: ReportDefinitionFields[];
    isUserReport: boolean;
    canViewInternalRisks: boolean;
    onClose: () => void;
}

interface DialogPage {
    number: number;
    params?: string;
    available: number;
}

const COMPONENT_NAME = 'add-edit-excel-listings-config';

const AddEditExcelListingsConfigDialog = ({
    onClose,
    addExcelListingsConfigAction,
    reportDefinition,
    reportDefinitions,
    isUserReport,
    canViewInternalRisks,
}: ExcelListingsConfigProps) => {
    const {
        t,
        i18n: { t: tRoot },
    } = useTranslation('ram', { keyPrefix: 'reporting.excelListings.dialogs.addEditExcelListingsConfig' });
    const { setComponentHasChanges, confirmDiscardChanges } = useUnsavedChangesContext();
    const step1NameRef = useRef<{ validationOnUpdate: () => boolean } | null>(null);
    const step2DataRef = useRef<{ validationOnUpdate: () => boolean } | null>(null);
    const disabledSave = useBoolean(false);
    const enabledClose = useBoolean(true);

    const emptyReportDefinition: ReportDefinitionFields = {
        __typename: 'ReportDefinition',
        id: Constants.ObjectId.Empty,
        userId: Constants.ObjectId.Empty,
        deleted: false,
        createdBy: null,
        updatedBy: null,
        created: null,
        updated: null,
        name: '',
        description: '',
        includeInternalRisks: false,
        includeNotApplicableRisks: false,
        includeObsoleteRisks: false,
        includeDraftRisks: false,
        recordId: null,
        tabs: [],
    };

    const initialState: AddEditCustomReportState = {
        reportDefinition: reportDefinition ? { ...reportDefinition } : { ...emptyReportDefinition },
        optionsTabs: reportDefinition?.tabs
            ? (reportDefinition?.tabs.filter((x) => x !== null) as ReportDefinitionFields_tabs[])
            : [],
    };

    const [page, setPage] = useState({
        number: CustomReportPage.step1Name,
        available: CustomReportPage.step1Name,
    } as DialogPage);
    const [errors, setErrors] = useState([] as AddEditCustomReportErrors[]);
    const [state, setState] = useState({ ...initialState } as AddEditCustomReportState);
    const [saveReportDefinition] = useMutation<upsertReportDefinition, upsertReportDefinitionVariables>(
        UPSERT_REPORT_DEFINITION,
    );
    const {
        loading: reportDefinitionOptionsLoading,
        error: reportDefinitionOptionsError,
        data: reportDefinitionOptionsData,
    } = useQuery<reportDefinitionOptions_OpraQueries>(GET_REPORT_DEFINITION_OPTIONS);

    const reportDefinitionOptions = reportDefinitionOptionsData?.reportDefinitionOptions;

    const hasChanges = useBoolean(false);

    const errorStateHasErrors = (errorState: AddEditCustomReportErrors) =>
        errorState && Object.keys(errorState).filter((item: string) => errorState[item]).length > 0;

    const currentPageHasErrors = () => errorStateHasErrors(errors[page.number]);

    const anyPageHasErrors = () =>
        errors.some((errorState: AddEditCustomReportErrors) => errorStateHasErrors(errorState));

    const onCloseHandler = async () => {
        if (!enabledClose.value) {
            const confirmed = await confirmDiscardChanges(`${COMPONENT_NAME}-unsaved-dialog`);
            if (!confirmed) {
                return;
            }
            setComponentHasChanges(COMPONENT_NAME, false);
        }
        onClose();
    };

    const updateErrorState = (errorState: AddEditCustomReportErrors, pageNumber: number) => {
        const errorsState = [...errors];
        errorsState[pageNumber] = errorState;
        setErrors(errorsState);
    };

    const isNameUnique = () => !checkIfNameExist(state.reportDefinition, reportDefinitions, 'name');

    const removeInternalRiskColumnFromTab = (tab: ReportDefinitionFields_tabs) =>
        removeColumnFromTab(tab, ReportDefinitionFieldTypes.RISK_INTERNAL);

    const addInternalRiskColumnToTab = (tab: ReportDefinitionFields_tabs) =>
        addColumnToTab(tab, ReportDefinitionFieldTypes.RISK_INTERNAL);

    const removeInternalRiskColumnByTabType = (tabs: ReportDefinitionFields_tabs[], tabType: ReportDefinitionTabs) => {
        updateTabByTabType(tabs, tabType, removeInternalRiskColumnFromTab);
    };

    const addInternalRiskColumnByTabType = (tabs: ReportDefinitionFields_tabs[], tabType: ReportDefinitionTabs) => {
        updateTabByTabType(tabs, tabType, addInternalRiskColumnToTab);
    };

    const applyFunctionToRiskInternalColumns = (
        updateFunction: (tabs: ReportDefinitionFields_tabs[], tabType: ReportDefinitionTabs) => void,
        tabs: ReportDefinitionFields_tabs[],
    ) => {
        updateFunction(tabs, ReportDefinitionTabs.RISKS);
        updateFunction(tabs, ReportDefinitionTabs.RISKS_AND_CONTROLS);
        updateFunction(tabs, ReportDefinitionTabs.RISKS_AND_CRITICAL_VARIABLES);
    };

    const updateRiskInternalColumns = (include: boolean) => {
        const updateFunction: (tabs: ReportDefinitionFields_tabs[], tabType: ReportDefinitionTabs) => void = include
            ? addInternalRiskColumnByTabType
            : removeInternalRiskColumnByTabType;

        const tabs = copyTabs(state.reportDefinition.tabs);

        applyFunctionToRiskInternalColumns(tabs, updateFunction);

        setState({
            ...state,
            reportDefinition: {
                ...state.reportDefinition,
                tabs: [...tabs],
                includeInternalRisks: include,
            },
        });
    };

    const removeRiskInternalColumnsFromOptionsTabs = () => {
        const tabs = copyTabs(state.optionsTabs);

        removeInternalRiskColumnByTabType(tabs, ReportDefinitionTabs.RISKS);
        removeInternalRiskColumnByTabType(tabs, ReportDefinitionTabs.RISKS_AND_CONTROLS);
        removeInternalRiskColumnByTabType(tabs, ReportDefinitionTabs.RISKS_AND_CRITICAL_VARIABLES);

        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/set-state-in-effect
        setState({
            ...state,
            optionsTabs: [...tabs],
        });
    };

    const getOptionsTabs = (reportDefinitionTabs: ReportDefinitionFields_tabs[]) => {
        const optionsTabs = copyTabs(reportDefinitionOptions?.tabs ?? []);

        removeMatchingColumns(optionsTabs, reportDefinitionTabs);

        return optionsTabs;
    };

    const getDefaultOptionsTab = (tabType: ReportDefinitionTabs) => {
        let tab = copyTabFromTabArray(reportDefinitionOptions.tabs, tabType);

        if (!state.reportDefinition.includeInternalRisks) tab = removeInternalRiskColumnFromTab(tab);

        return tab;
    };

    const reset = () => {
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        /* eslint-disable @eslint-react/set-state-in-effect */
        if (reportDefinition && reportDefinitionOptions) {
            setState({
                reportDefinition: copyReportDefinition(reportDefinition),
                optionsTabs: getOptionsTabs(reportDefinition.tabs),
            });
        } else if (!reportDefinition && reportDefinitionOptions) {
            setState({
                ...initialState,
                optionsTabs: copyTabs(reportDefinitionOptions.tabs),
            });
        }
        /* eslint-enable @eslint-react/set-state-in-effect */
    };

    const resetTabs = () => {
        if (reportDefinition && reportDefinitionOptions)
            setState({
                reportDefinition: {
                    ...state.reportDefinition,
                    tabs: copyTabs(reportDefinition.tabs),
                },
                optionsTabs: getOptionsTabs(reportDefinition.tabs),
            });
        else if (!reportDefinition && reportDefinitionOptions) {
            setState({
                reportDefinition: {
                    ...state.reportDefinition,
                    tabs: [],
                },
                optionsTabs: copyTabs(reportDefinitionOptions.tabs),
            });
        }
    };

    const resetColumns = (tabType: ReportDefinitionTabs) => {
        const tabs = copyTabs(state.reportDefinition.tabs);
        const currentTab = getTab(tabs, tabType);
        const resetTab = reportDefinition ? getTab(reportDefinition.tabs, tabType) : getDefaultOptionsTab(tabType);

        currentTab.columns = [...resetTab.columns];

        setState({
            reportDefinition: {
                ...state.reportDefinition,
                tabs,
            },
            optionsTabs: getOptionsTabs(tabs),
        });
    };

    const back = () => {
        setPage({ ...page, number: page.number - 1 });
    };

    const close = () => {
        setPage({ number: CustomReportPage.step1Name, available: CustomReportPage.step1Name });
        reset();
        setErrors([]);
        onClose();
    };

    const getReportDefinitionInputColumns = (
        columns: ReportDefinitionFields_tabs_columns[],
    ): ReportDefinitionColumnInput[] =>
        columns.map(
            (column: ReportDefinitionFields_tabs_columns) =>
                ({
                    dataField: column.dataField,
                    index: 0,
                }) as ReportDefinitionColumnInput,
        );

    const getReportDefinitionInputTabs = (tabs: ReportDefinitionFields_tabs[]): ReportDefinitionTabInput[] =>
        tabs.map(
            (tab: ReportDefinitionFields_tabs) =>
                ({
                    tabType: tab.tabType,
                    columns: getReportDefinitionInputColumns(tab.columns),
                }) as ReportDefinitionTabInput,
        );

    const getReportDefinitionInput = (): ReportDefinitionInput => ({
        id: state.reportDefinition.id,
        userId: state.reportDefinition.userId,
        name: state.reportDefinition.name,
        description: state.reportDefinition.description,
        includeInternalRisks: state.reportDefinition.includeInternalRisks,
        includeNotApplicableRisks: state.reportDefinition.includeNotApplicableRisks,
        includeDraftRisks: state.reportDefinition.includeDraftRisks,
        includeObsoleteRisks: state.reportDefinition.includeObsoleteRisks,
        recordId: state.reportDefinition.recordId,
        tabs: getReportDefinitionInputTabs(state.reportDefinition.tabs),
    });

    const onSave = () => {
        if (
            anyPageHasErrors() ||
            !step1NameRef.current?.validationOnUpdate() ||
            !step2DataRef.current?.validationOnUpdate()
        ) {
            ShowError(tRoot('errorMessage'));
            return;
        }

        if (reportDefinition && !hasChanges.value) {
            ShowSuccess(tRoot('actionsSuccessfulToast'));
            close();
            return;
        }
        disabledSave.setTrue();
        saveReportDefinition({
            variables: {
                reportDefinition: getReportDefinitionInput(),
                isUserReport,
            },
            refetchQueries: [
                {
                    query: GET_REPORT_DEFINITIONS,
                },
            ],
        })
            .then((result) => {
                disabledSave.setFalse();

                if (!result?.data?.upsertReportDefinition) {
                    ShowError(tRoot('errorMessage'));
                    return;
                }
                ShowSuccess(tRoot('actionsSuccessfulToast'));
                setComponentHasChanges(COMPONENT_NAME, false);
                close();
            })
            .catch(() => {
                ShowError(tRoot('errorMessage'));
                disabledSave.setFalse();
            });
    };

    const handleNextButton = () => {
        if (page.number === CustomReportPage.step1Name && step1NameRef.current?.validationOnUpdate())
            setPage({ number: CustomReportPage.step2Data, available: CustomReportPage.step2Data });
        else if (page.number === CustomReportPage.step2Data && step2DataRef.current?.validationOnUpdate())
            setPage({ number: CustomReportPage.step3Columns, available: CustomReportPage.step3Columns });
        else if (page.number === CustomReportPage.step3Columns) onSave();
    };

    useEffect(() => {
        if (!state.reportDefinition.includeInternalRisks) removeRiskInternalColumnsFromOptionsTabs();
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [state.reportDefinition.tabs]);

    useEffect(() => {
        reset();
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [reportDefinition, reportDefinitionOptions]);

    useEffect(() => {
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/set-state-in-effect
        if (reportDefinition) setPage({ number: CustomReportPage.step1Name, available: CustomReportPage.step3Columns });
    }, [reportDefinition]);

    useEffect(() => {
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/set-state-in-effect
        if (currentPageHasErrors()) setPage({ ...page, available: page.number });
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [errors]);

    useEffect(() => {
        const initialStateTabs = getReportDefinitionInputTabs(initialState.reportDefinition.tabs);
        const stateTabs = getReportDefinitionInputTabs(state.reportDefinition.tabs);

        const initialStateTabsJson = JSON.stringify(initialStateTabs);
        const stateTabsJson = JSON.stringify(stateTabs);

        const fieldsChanged =
            initialState.reportDefinition.id !== state.reportDefinition.id ||
            initialState.reportDefinition.name !== state.reportDefinition.name ||
            initialState.reportDefinition.includeInternalRisks !== state.reportDefinition.includeInternalRisks ||
            initialState.reportDefinition.includeNotApplicableRisks !==
                state.reportDefinition.includeNotApplicableRisks ||
            initialState.reportDefinition.includeDraftRisks !== state.reportDefinition.includeDraftRisks ||
            initialState.reportDefinition.includeObsoleteRisks !== state.reportDefinition.includeObsoleteRisks ||
            initialState.reportDefinition.description !== state.reportDefinition.description;

        hasChanges.setValue(initialStateTabsJson !== stateTabsJson || fieldsChanged);
        setComponentHasChanges(COMPONENT_NAME, initialStateTabsJson !== stateTabsJson || fieldsChanged);
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [state]);

    const compareReportFields = (object1: ReportDefinitionFields, object2: ReportDefinitionFields) => {
        // check if changes in dialog were made
        const keys1 = Object.keys(object1);
        for (let i = 0; i < keys1.length; i++) {
            if (!objectEquals(object1[keys1[i]], object2[keys1[i]])) {
                return false;
            }
        }
        return true;
    };

    useEffect(() => {
        if (reportDefinition) enabledClose.setValue(compareReportFields(state.reportDefinition, reportDefinition));

        if (!reportDefinition)
            enabledClose.setValue(compareReportFields(state.reportDefinition, initialState.reportDefinition));
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [state.reportDefinition, reportDefinition]);

    const handleChangeStep = (event: { action: string }) => {
        if (event.action === 'back') back();
        if (event.action === 'next' && !currentPageHasErrors()) handleNextButton();
    };

    const dialogTitle = addExcelListingsConfigAction
        ? `${tRoot('common:add')} ${t('title')}`
        : `${tRoot('common:edit')} ${t('title')}: ${reportDefinition?.recordId}`;
    const primaryBtnLabel = addExcelListingsConfigAction ? tRoot('common:add') : tRoot('common:update');
    // eslint-disable-next-line max-len
    const createdBy = `${stringDateWithoutTimeFromUtcNonNullable(reportDefinition?.created, 'DD-MMM-YYYY')} by ${reportDefinition?.createdBy?.firstName} ${reportDefinition?.createdBy?.lastName}`;
    // eslint-disable-next-line max-len
    const updatedBy = `${stringDateWithoutTimeFromUtcNonNullable(reportDefinition?.updated, 'DD-MMM-YYYY')} by ${reportDefinition?.updatedBy?.firstName} ${reportDefinition?.updatedBy?.lastName}`;

    if (reportDefinitionOptionsLoading) return <Loading />;

    if (reportDefinitionOptionsError) return <ErrorMessage />;

    const stepTitle = t(`steps.${page.number.toString()}.title`);

    return (
        <DraggableDialog
            className={cn(styles.kWindowContent, 'add-risk-evaluation-dialog')}
            dataTestId="add-edit-excel-listings-config-dialog"
            title={dialogTitle}
            onClose={onCloseHandler}
            onSubmit={() => onSave()}
            primaryBtnLabel={primaryBtnLabel}
            primaryBtnDisabled={enabledClose.value}
            initialHeight={addExcelListingsConfigAction ? 585 : 630}
            initialWidth={700}
            createdUpdatedInfo={
                reportDefinition && !addExcelListingsConfigAction ? (
                    <CreatedUpdatedInfo created={createdBy} updated={updatedBy} />
                ) : undefined
            }
            createdUpdatedBackgroundColour="gray"
            stepCount={3}
            currentStep={page.number}
            currentStepTitle={stepTitle}
            onStepAction={handleChangeStep}
            bodyPadding="none"
        >
            <CustomReportStep1Name
                show={page.number === CustomReportPage.step1Name}
                reportDefinition={state.reportDefinition}
                state={state}
                setState={setState}
                updateErrorState={updateErrorState}
                isNameUnique={isNameUnique}
                canViewInternalRisks={canViewInternalRisks}
                updateRiskInternalColumns={updateRiskInternalColumns}
                ref={step1NameRef}
            />
            <CustomReportStep2Data
                show={page.number === CustomReportPage.step2Data}
                optionsTabs={state.optionsTabs}
                reportDefinition={state.reportDefinition}
                state={state}
                setState={setState}
                getDefaultOptionsTab={getDefaultOptionsTab}
                reset={resetTabs}
                updateErrorState={updateErrorState}
                ref={step2DataRef}
            />
            <CustomReportStep3Columns
                show={page.number === CustomReportPage.step3Columns}
                optionsTabs={state.optionsTabs}
                reportDefinition={state.reportDefinition}
                state={state}
                setState={setState}
                resetColumns={resetColumns}
            />
        </DraggableDialog>
    );
};

export default AddEditExcelListingsConfigDialog;
